import { CodeBracketIcon, EyeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { type FC, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

import { highlightChildren, highlightComponents } from "@components/EvalReview/highlightText";
import {
  canonicalizeLlmOutput,
  comparableLlmText,
  tryExtractMarkdownSections,
  tryExtractTableRows,
  tryParseStructured,
} from "@components/LlmOutput/outputShapes";
import {
  type DiffSegment,
  DiffType,
  type DiffTypeValue,
  textSimilarity,
  wordDiff,
} from "@components/LlmOutput/wordDiff";

/** One renderer for LLM output on every surface: JSON array of objects →
 *  table, JSON object of strings → sectioned markdown, anything else →
 *  markdown. The Raw toggle keeps the verbatim text one click away. */

type Highlights = readonly string[];
const NO_HIGHLIGHTS: Highlights = [];

export const TableResultView: FC<{ rows: Record<string, unknown>[]; highlights?: Highlights }> = ({
  rows,
  highlights = NO_HIGHLIGHTS,
}) => {
  const columns = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const renderCell = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
    return JSON.stringify(v);
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-limestone-50">
            {columns.map((c) => (
              <th key={c} className="border border-border-subtle px-2 py-1 text-left font-semibold text-text-primary">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="align-top">
              {columns.map((c) => (
                <td key={c} className="whitespace-pre-wrap border border-border-subtle px-2 py-1 text-text-primary">
                  {highlightChildren(renderCell(row[c]), highlights)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const isRowArray = (value: unknown): value is Record<string, unknown>[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((row) => row !== null && typeof row === "object" && !Array.isArray(row));

/** Any JSON shape, rendered the way the template sandbox renders it:
 *  row-arrays as tables (however deep they nest — Services_Table.data
 *  included), strings as markdown, objects as labelled sections. */
const StructuredValue: FC<{ value: unknown; highlights: Highlights }> = ({ value, highlights }) => {
  if (typeof value === "string") return <Markdown text={value} highlights={highlights} />;
  if (isRowArray(value)) return <TableResultView rows={value} highlights={highlights} />;
  if (Array.isArray(value)) return <Markdown text={value.map((v) => String(v)).join("\n\n")} highlights={highlights} />;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    // {data: [rows]} is a bare envelope — the table IS the content.
    if (entries.length === 1 && entries[0][0] === "data" && isRowArray(entries[0][1])) {
      return <TableResultView rows={entries[0][1]} highlights={highlights} />;
    }
    return (
      <div className="flex flex-col gap-3">
        {entries.map(([key, nested]) => (
          <div key={key}>
            <span className="text-xs font-medium text-text-tertiary">{key}</span>
            <StructuredValue value={nested} highlights={highlights} />
          </div>
        ))}
      </div>
    );
  }
  return <Markdown text={String(value ?? "")} highlights={highlights} />;
};

const Markdown: FC<{ text: string; highlights: Highlights }> = ({ text, highlights }) => (
  <div className="prose prose-sm max-w-none text-text-primary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
    <ReactMarkdown remarkPlugins={[remarkBreaks]} components={highlightComponents(highlights)}>
      {text}
    </ReactMarkdown>
  </div>
);

// Segmented control: bordered group, filled active segment.
const toggleClasses = (active: boolean) =>
  `flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors ${
    active ? "bg-limestone-900 text-white" : "bg-bg-surface text-text-secondary hover:bg-bg-default"
  }`;

// Design-system positive/negative pairings (same as the check chips).
const DIFF_SEGMENT_CLASSES: Record<DiffTypeValue, string> = {
  [DiffType.Equal]: "",
  [DiffType.Added]: "rounded-sm bg-green-100 text-green-700",
  [DiffType.Removed]: "rounded-sm bg-red-100 text-red-600 line-through",
};

const DiffText: FC<{ segments: DiffSegment[] }> = ({ segments }) => (
  <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-text-primary">
    {segments.map((segment, i) => (
      <span key={i} className={DIFF_SEGMENT_CLASSES[segment.type]}>
        {segment.text}
      </span>
    ))}
  </pre>
);

/** Sectioned diff when both sides share the object-of-strings shape, flat
 *  word-diff otherwise; null when the texts are too large to pair. */
const buildDiff = (before: string, after: string): { key: string | null; segments: DiffSegment[] }[] | null => {
  // Sort by key so differing section order still diffs matching sections.
  const byKey = (sections: [string, string][]) => [...sections].sort(([a], [b]) => a.localeCompare(b));
  const beforeSections = tryExtractMarkdownSections(before);
  const afterSections = tryExtractMarkdownSections(after);
  if (
    beforeSections &&
    afterSections &&
    JSON.stringify(byKey(beforeSections).map(([k]) => k)) === JSON.stringify(byKey(afterSections).map(([k]) => k))
  ) {
    const sortedBefore = byKey(beforeSections);
    const perSection = byKey(afterSections).map(([key, text], index) => ({
      key,
      segments: wordDiff(sortedBefore[index][1], text),
    }));
    if (perSection.every((s) => s.segments !== null)) {
      return perSection as { key: string; segments: DiffSegment[] }[];
    }
  }
  const flat = wordDiff(before, after);
  return flat === null ? null : [{ key: null, segments: flat }];
};

const Mode = { Preview: "preview", Diff: "diff", Raw: "raw" } as const;
type ModeValue = (typeof Mode)[keyof typeof Mode];

// Below this similarity a word diff is red/green confetti; the similarity
// chip carries the message instead.
const MIN_DIFFABLE_SIMILARITY = 0.5;

interface LlmOutputViewProps {
  result: string | null;
  /** The other side of a comparison, enabling an opt-in Diff view. Preview
   *  stays the default; Diff appears only where a word diff is readable —
   *  never for table output or mostly-different texts. */
  diffAgainst?: string | null;
  /** Quotes to mark (e.g. a check's spans). Marked in Preview and Raw. */
  highlights?: Highlights;
}

export const LlmOutputView: FC<LlmOutputViewProps> = ({ result, diffAgainst, highlights = NO_HIGHLIGHTS }) => {
  const [mode, setMode] = useState<ModeValue>(Mode.Preview);

  // Memoized: these JSON.parse whole outputs, and the comparison screen
  // re-renders every card per notes keystroke.
  const structured = useMemo(() => tryParseStructured(result), [result]);

  const diff = useMemo(() => {
    if (diffAgainst == null || result == null) return null;
    if (canonicalizeLlmOutput(diffAgainst) === canonicalizeLlmOutput(result)) return null;
    if (tryExtractTableRows(result) || tryExtractTableRows(diffAgainst)) return null;
    const similarity = textSimilarity(comparableLlmText(diffAgainst), comparableLlmText(result));
    if (similarity === null || similarity < MIN_DIFFABLE_SIMILARITY) return null;
    return buildDiff(diffAgainst, result);
  }, [diffAgainst, result]);
  // Plain prose has no second reading — only structured output earns the toggle.
  const hasRawView = structured !== null;
  const showToggle = hasRawView || diff !== null;
  const effectiveMode = mode === Mode.Diff && diff === null ? Mode.Preview : mode;

  return (
    <div className="flex flex-col gap-2">
      {showToggle ? (
        <div className="flex items-center justify-end">
          <div className="inline-flex divide-x divide-border-medium overflow-hidden rounded-md border border-border-medium shadow-sm">
            <button
              type="button"
              onClick={() => setMode(Mode.Preview)}
              className={toggleClasses(effectiveMode === Mode.Preview)}
            >
              <EyeIcon className="size-3" />
              Preview
            </button>
            {diff !== null ? (
              <button
                type="button"
                onClick={() => setMode(Mode.Diff)}
                className={toggleClasses(effectiveMode === Mode.Diff)}
              >
                <PlusIcon className="size-3" />
                Diff
              </button>
            ) : null}
            {hasRawView ? (
              <button
                type="button"
                onClick={() => setMode(Mode.Raw)}
                className={toggleClasses(effectiveMode === Mode.Raw)}
              >
                <CodeBracketIcon className="size-3" />
                Raw
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {effectiveMode === Mode.Diff && diff !== null ? (
        <div className="flex flex-col gap-3">
          {diff.map(({ key, segments }) => (
            <div key={key ?? "__flat__"}>
              {key !== null ? <span className="text-xs font-medium text-text-tertiary">{key}</span> : null}
              <DiffText segments={segments} />
            </div>
          ))}
        </div>
      ) : effectiveMode === Mode.Raw && hasRawView ? (
        <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed text-text-primary">
          {highlightChildren(result ?? "(no output)", highlights)}
        </pre>
      ) : structured !== null ? (
        <StructuredValue value={structured} highlights={highlights} />
      ) : (
        <Markdown text={result ?? "(no output)"} highlights={highlights} />
      )}
    </div>
  );
};
